import { BoolNode, SearchAtom } from 'src/services/common.types';

// ----------------------------------------------------------------------

// Boolean operators the keyword search understands (item 23); they are not literal terms to highlight.
const OPERATORS = new Set(['AND', 'OR', 'NOT']);

/**
 * Split a raw keyword search string (as typed into the advanced-search keyword field, carried on the
 * `?q=` param) into the individual terms to highlight in resume text (spec item 18, JT1-1537). Quoted
 * phrases are kept whole; boolean operators and one-character noise are dropped. Returns an empty
 * array when there is nothing to highlight.
 */
export const parseHighlightTerms = (raw?: string | null): string[] => {
  if (!raw) return [];

  const tokens = raw.match(/"[^"]+"|\S+/g) ?? [];

  return tokens
    .map((token) => token.replace(/^"|"$/g, '').trim())
    .filter((term) => term.length >= 2 && !OPERATORS.has(term.toUpperCase()));
};

/**
 * Advanced-search fields whose values are free text a resume can contain. Labels (stored as ids),
 * enums (availability, work rights, education level) and numbers (salary, notice period) never
 * appear in resume text, so they are left out.
 */
const HIGHLIGHT_FIELDS = new Set([
  'text_query',
  'role_title',
  'company',
  'skills',
  'location',
  'education_institution',
  'executive_summary',
]);

/**
 * Build the `?q=` highlight string for a record opened from Advanced Search, so its resume highlights
 * every term the recruiter searched on: the keyword (folded into the DSL as a `text_query` atom) and
 * the criteria rows. Terms under a NOT are skipped, because an excluded term is the opposite of a
 * match. The result is plain space-separated text for `parseHighlightTerms` to split.
 */
export const highlightQueryFromDsl = (dsl?: BoolNode | null): string => {
  // Keyed case-insensitively so "Agile" as a skill and "agile" as a keyword ride the URL once.
  const terms = new Map<string, string>();

  const visit = (node: BoolNode | SearchAtom) => {
    if ('op' in node) {
      if (node.op !== 'NOT') (node.children ?? []).forEach(visit);
      return;
    }
    if (!HIGHLIGHT_FIELDS.has(node.field)) return;

    (Array.isArray(node.value) ? node.value : [node.value]).forEach((value) => {
      const term = typeof value === 'string' ? value.trim() : '';
      if (term && !terms.has(term.toLowerCase())) terms.set(term.toLowerCase(), term);
    });
  };

  if (dsl) visit(dsl);

  return Array.from(terms.values()).join(' ');
};
